import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Target } from "lucide-react";

function istMonth(delta = 0) {
  const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit" });
  const parts = fmt.formatToParts(new Date());
  const y = Number(parts.find((p) => p.type === "year").value);
  const m = Number(parts.find((p) => p.type === "month").value);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-IN", { month: "long", year: "numeric", timeZone: "UTC" });
}

export default function SuperSalesTargets() {
  const [month, setMonth] = useState(() => istMonth());
  const [sheet, setSheet] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = (m) => {
    api.get("/platform/sales-targets", { params: { month: m } })
      .then(({ data }) => {
        setSheet(data);
        const next = {};
        (data.items || []).forEach((p) => {
          next[p.sales_user_id] = {
            target_count: p.has_target ? p.target_count : "",
            target_amount: p.has_target ? p.target_amount : "",
            email: p.email || "",
          };
        });
        setDrafts(next);
      })
      .catch((e) => toast.error(extractErr(e)));
  };

  useEffect(() => { load(month); }, [month]);

  const setDraft = (id, key, value) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [key]: value } }));

  const save = async (person) => {
    const draft = drafts[person.sales_user_id] || {};
    try {
      const { data } = await api.put(`/platform/sales-targets/${person.sales_user_id}`, {
        month,
        target_count: Number(draft.target_count || 0),
        target_amount: Number(draft.target_amount || 0),
        email: draft.email || "",
      });
      setSheet(data);
      toast.success(`Saved ${person.name}`);
    } catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <div className="space-y-5 max-w-6xl">
      <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-indigo-800 text-white p-6">
        <div className="flex items-center gap-2 text-indigo-200 text-xs uppercase tracking-[0.18em]"><Target className="w-4 h-4" /> Monthly sheet</div>
        <h1 className="font-display text-3xl font-bold mt-1">Sales targets</h1>
        <p className="text-indigo-100 text-sm mt-2">Set how many orders and how much money each salesperson should close this month. A new month starts at zero until you set it. Add a reminder email so follow-up dates can email them.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => setMonth((m) => shiftMonth(m, -1))}>Previous</Button>
        <Button type="button" variant="outline" onClick={() => setMonth((m) => shiftMonth(m, 1))}>Next</Button>
        {[0, -1, -2, -3, -4, -5].map((n) => {
          const id = istMonth(n);
          return (
            <button key={id} type="button" onClick={() => setMonth(id)} className={`rounded-full px-3 py-1 text-xs font-semibold border ${month === id ? "bg-slate-900 text-white" : "bg-white"}`}>
              {monthLabel(id)}
            </button>
          );
        })}
      </div>
      <div className="overflow-x-auto rounded-2xl border bg-white" data-testid="sales-target-sheet">
        <table className="min-w-[860px] w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              {["Sales person", "Target no.", "Total target amount", "Reminder email", "Archive no.", "Archive amount", ""].map((h) => <th key={h || "save"} className="px-3 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(sheet?.items || []).map((p) => {
              const draft = drafts[p.sales_user_id] || {};
              return (
                <tr key={p.sales_user_id} className="border-t">
                  <td className="px-3 py-3 font-semibold">{p.name}<div className="text-[11px] text-slate-400 font-normal">{p.username}</div></td>
                  <td className="px-3 py-3"><Input type="number" min="0" className="h-8 w-24" data-testid={`sales-target-count-${p.sales_user_id}`} value={draft.target_count} onChange={(e) => setDraft(p.sales_user_id, "target_count", e.target.value)} /></td>
                  <td className="px-3 py-3"><Input type="number" min="0" className="h-8 w-32" data-testid={`sales-target-amount-${p.sales_user_id}`} value={draft.target_amount} onChange={(e) => setDraft(p.sales_user_id, "target_amount", e.target.value)} /></td>
                  <td className="px-3 py-3"><Input type="email" className="h-8 w-52" placeholder="name@email.com" data-testid={`sales-target-email-${p.sales_user_id}`} value={draft.email} onChange={(e) => setDraft(p.sales_user_id, "email", e.target.value)} /></td>
                  <td className="px-3 py-3">{p.archive_count}</td>
                  <td className="px-3 py-3">₹{Number(p.archive_amount || 0).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-3"><Button type="button" className="bg-indigo-700 hover:bg-indigo-800 h-8" data-testid={`sales-target-save-${p.sales_user_id}`} onClick={() => save(p)}>Save</Button></td>
                </tr>
              );
            })}
            {(sheet?.items || []).length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No sales people yet. Add them under Sales users.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
